/**
 * The Cloudflare Turnstile widget, with its lifetime managed rather than left to
 * chance.
 *
 * Three things have to be right or this integration quietly stops working:
 *
 *   - Exactly one widget per container. React's strict mode mounts, unmounts and
 *     remounts every effect in development, so the cleanup removes the widget it
 *     created and an in-flight render is abandoned if its effect was torn down
 *     before Cloudflare answered.
 *   - A fresh token per request. Tokens are single use and expire on their own
 *     after a few minutes, so the parent bumps `resetSignal` after every settled
 *     attempt and the widget hands back a new one.
 *   - An honest failure. If the script cannot be reached — an ad blocker, a
 *     captive portal, a bad network — the form says so and offers a retry
 *     instead of leaving a submit button that will always be refused.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { loadTurnstile } from '../lib/turnstile.js';
import type { TurnstileApi } from '../lib/turnstile.js';

export type TurnstileStatus = 'loading' | 'ready' | 'solved' | 'failed';

export interface TurnstileWidgetProps {
  siteKey: string;
  /** Called with a token that is good for exactly one API request. */
  onToken: (token: string) => void;
  /** Called when the token is spent, expired or rejected, so the parent drops it. */
  onClear: () => void;
  onStatusChange?: (status: TurnstileStatus) => void;
  /** Change this to discard the current challenge and ask for a new one. */
  resetSignal: number;
  /** Recorded against the Turnstile analytics for this site key. */
  action?: string;
  label?: string;
}

export function TurnstileWidget({
  siteKey,
  onToken,
  onClear,
  onStatusChange,
  resetSignal,
  action = 'login',
  label = 'Human check',
}: TurnstileWidgetProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<TurnstileApi | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<TurnstileStatus>('loading');

  // Held in a ref so a parent that re-renders on every keystroke does not tear
  // down and rebuild the widget under the user's cursor.
  const handlersRef = useRef({ onToken, onClear, onStatusChange });
  useEffect(() => {
    handlersRef.current = { onToken, onClear, onStatusChange };
  }, [onToken, onClear, onStatusChange]);

  const report = useCallback((next: TurnstileStatus) => {
    setStatus(next);
    handlersRef.current.onStatusChange?.(next);
  }, []);

  useEffect(() => {
    let abandoned = false;
    report('loading');

    loadTurnstile()
      .then((api) => {
        const container = containerRef.current;
        if (abandoned || container === null) return;

        apiRef.current = api;
        const widgetId = api.render(container, {
          sitekey: siteKey,
          action,
          theme: 'auto',
          retry: 'auto',
          callback: (token: string) => {
            handlersRef.current.onToken(token);
            report('solved');
          },
          'expired-callback': () => {
            handlersRef.current.onClear();
            report('ready');
          },
          'timeout-callback': () => {
            handlersRef.current.onClear();
            report('ready');
          },
          'error-callback': () => {
            handlersRef.current.onClear();
            report('failed');
          },
        });

        if (widgetId === undefined) {
          report('failed');
          return;
        }

        widgetIdRef.current = widgetId;
        report('ready');
      })
      .catch(() => {
        if (!abandoned) report('failed');
      });

    return () => {
      abandoned = true;
      const api = apiRef.current;
      const widgetId = widgetIdRef.current;
      widgetIdRef.current = null;
      if (api === null || widgetId === null) return;
      try {
        api.remove(widgetId);
      } catch {
        // Cloudflare throws if the widget is already gone, which is the state
        // we were asking for.
      }
    };
  }, [siteKey, action, attempt, report]);

  // Skipped on the first run: the widget that has just rendered is already
  // fresh, and resetting it immediately would throw away a solved challenge.
  const lastResetRef = useRef(resetSignal);
  useEffect(() => {
    if (resetSignal === lastResetRef.current) return;
    lastResetRef.current = resetSignal;

    const api = apiRef.current;
    const widgetId = widgetIdRef.current;
    if (api === null || widgetId === null) return;
    try {
      api.reset(widgetId);
      report('ready');
    } catch {
      report('failed');
    }
  }, [resetSignal, report]);

  return (
    <div className="turnstile">
      <span className="field__label">{label}</span>
      <div className="turnstile__widget" ref={containerRef} />

      {status === 'loading' ? (
        <p className="turnstile__note" role="status">
          Loading the human check…
        </p>
      ) : null}

      {status === 'failed' ? (
        <p className="turnstile__note turnstile__note--danger" role="alert">
          The human check did not load. An ad blocker or a restricted network is the usual
          cause.{' '}
          <button
            type="button"
            className="button button--link"
            onClick={() => setAttempt((value) => value + 1)}
          >
            Try again
          </button>
        </p>
      ) : null}
    </div>
  );
}
